import { ImageFit, Visibility } from '@bigcommerce/page-builder-sdk';

import { ActionLabels, ActionTypes } from '../types/actions';
import { ProductCardProps, ProductProps } from '../types/productProps';
import { ProductLocales } from '../types/productDynamicData';

import { DEFAULT_CURRENCY, DEFAULT_LOCALE, DEFAULT_PRODUCT_TITLE, TAX_OPTIONS } from './constants';

// Normalize a locale subfolder to a single leading slash and no trailing slash (e.g. "de/" -> "/de").
const normalizeSubfolder = (localeSubfolder: string): string => `/${localeSubfolder.replace(/^\/+|\/+$/g, '')}`;

// On the default locale `path` is already correct (no subfolder). On other locales the correct link is the
// per-locale path from `product.locales` — this handles translated slugs, where the vanity path differs by locale
// (e.g. "/blue-shirt" vs "/de/blaues-hemd"). If `locales` is unavailable (e.g. the locale-alternates experiment is
// off, so the field resolves to null), fall back to prepending the current locale's subfolder to `path`.
const resolveLocalizedPath = (path: string, locales: ProductLocales | undefined, localeSubfolder?: string): string => {
    if (!path || !localeSubfolder) {
        return path;
    }

    const subfolder = normalizeSubfolder(localeSubfolder);

    const edges = locales && locales.edges ? locales.edges : [];
    const match = edges.find(edge => edge.node.path === subfolder || edge.node.path.startsWith(`${subfolder}/`));
    if (match) {
        return match.node.path;
    }

    // Fallback: no matching alternate — prepend the subfolder, guarding against double-prefixing.
    if (path === subfolder || path.startsWith(`${subfolder}/`)) {
        return path;
    }

    return `${subfolder}${path}`;
};

const buildProductCard = (props: ProductProps): ProductCardProps => {
    const {
        button: { buttonActionType, visibility },
        productImage,
    } = props;
    const activeCurrency = props._.context && props._.context.activeCurrencyCode;
    const localeSubfolder = props._.context && props._.context.localeSubfolder;
    const productProps = {
        actionLabel: ActionLabels[buttonActionType],
        actionLink: '#',
        brand: 'Brand',
        buttonVisibility: visibility,
        currencyCode: activeCurrency || DEFAULT_CURRENCY,
        displayLocale: props.displayLocale || DEFAULT_LOCALE,
        path: '#',
        name: DEFAULT_PRODUCT_TITLE,
        plpTaxSetting: TAX_OPTIONS.EXCLUDED,
        priceWithTax: Math.floor(Math.random() * 4000) / 10.0,
        priceWithoutTax: Math.floor(Math.random() * 4000) / 10.0,
        productImage: undefined,
        productImageFit: productImage.imageFit || ImageFit.Fit,
        rating: 2.5 + Math.floor(Math.random() * 5) / 2,
        starColorEmpty: props.productRating.starColorEmpty,
        starColorFilled: props.productRating.starColorFilled,
        totalReviews: Math.floor(Math.random() * 50),
    };

    const currencyNode = props._.data && props._.data.site && props._.data.site.currency;
    const settingsNode = props._.data && props._.data.site && props._.data.site.settings;
    const productNode = props._.data && props._.data.site && props._.data.site.product;

    const plpTaxSetting = settingsNode ? settingsNode.tax.plp : TAX_OPTIONS.EXCLUDED;

    if (productNode) {
        const {
            defaultImage,
            name,
            path,
            priceWithTax: priceWithTaxNode,
            priceWithoutTax: priceWithoutTaxNode,
        } = productNode;

        const priceWithTax = priceWithTaxNode ? priceWithTaxNode.price.value : undefined;
        const priceWithoutTax = priceWithoutTaxNode ? priceWithoutTaxNode.price.value : undefined;
        const currencyCode = priceWithoutTaxNode ? priceWithoutTaxNode.price.currencyCode : undefined;
        const brand = productNode.brand ? productNode.brand.name : '';
        const { summationOfRatings, numberOfReviews } = productNode.reviewSummary;
        const rating = summationOfRatings / numberOfReviews || 0;
        const localizedPath = resolveLocalizedPath(path, productNode.locales, localeSubfolder);

        let { actionLink } = productProps;
        if (buttonActionType === ActionTypes.AddToCard) {
            actionLink = productNode.addToCartUrl;
            if (typeof priceWithoutTax !== 'number') {
                productProps.buttonVisibility = Visibility.Hide;
            }
        } else if (buttonActionType === ActionTypes.AddToWishlist) {
            actionLink = productNode.addToWishlistUrl;
        } else if (buttonActionType === ActionTypes.GoToProduct) {
            actionLink = localizedPath;
        }

        const productNodeImage = defaultImage ? defaultImage.url : productProps.productImage;

        return {
            ...productProps,
            actionLink,
            brand,
            currencyCode,
            currencyDisplay: currencyNode,
            name,
            path: localizedPath,
            plpTaxSetting,
            priceWithTax,
            priceWithoutTax,
            productImage: productNodeImage,
            rating,
            totalReviews: numberOfReviews,
        };
    }

    return productProps;
};

export default buildProductCard;
